// src/api/permisosService.js - VERSIÓN COMPLETA CORREGIDA
import { obtenerPermisosPorRolConEstado } from './permiso.js';
import { obtenerModulos } from './modulo.js';

// Cache de permisos
let permisosCache = null;
let modulosCache = null;
let lastFetch = 0;
const CACHE_DURATION = 5000; // 5 segundos para pruebas (cambiar a 5 minutos después)

/**
 * Cargar permisos del usuario desde la API
 * @param {number} idRol - ID del rol del usuario
 * @param {boolean} forceRefresh - Forzar recarga desde API
 * @returns {Promise} Permisos del usuario
 */
export const cargarPermisosDesdeAPI = async (idRol, forceRefresh = false) => {
  try {
    if (!idRol) {
      console.warn('⚠️ No se proporcionó ID de rol');
      return null;
    }
    
    console.log(`🔍 Cargando permisos para rol ID: ${idRol} desde API${forceRefresh ? ' (FORZADO)' : ''}`);
    
    // Verificar caché - SI forceRefresh ES TRUE, IGNORAR CACHÉ
    const now = Date.now();
    if (!forceRefresh && permisosCache && lastFetch > 0 && (now - lastFetch) < CACHE_DURATION) {
      console.log('📦 Usando permisos en caché');
      console.log('📦 Permisos cacheados:', permisosCache);
      return permisosCache;
    }
    
    // Obtener permisos del rol
    console.log('📡 Llamando a API: obtenerPermisosPorRolConEstado');
    const permisosResponse = await obtenerPermisosPorRolConEstado(idRol);
    
    console.log('📡 Respuesta de permisos:', permisosResponse);
    
    if (!permisosResponse.success) {
      throw new Error(permisosResponse.error || 'Error al cargar permisos');
    }
    
    // Obtener módulos activos
    console.log('📡 Llamando a API: obtenerModulos');
    const modulosResponse = await obtenerModulos('activos');
    
    console.log('📡 Respuesta de módulos:', modulosResponse);
    
    if (!modulosResponse.success) {
      throw new Error(modulosResponse.error || 'Error al cargar módulos');
    }
    
    // Construir objeto de permisos
    const permisos = {};
    const modulos = modulosResponse.data || [];
    const permisosData = permisosResponse.data || [];
    
    console.log(`📦 Módulos activos: ${modulos.length}`);
    console.log(`🔑 Permisos totales recibidos: ${permisosData.length}`);
    console.log(`🔑 Permisos asignados: ${permisosData.filter(p => p.asignado).length}`);
    
    // Crear mapa de módulos por ID
    const modulosMap = {};
    modulos.forEach(modulo => {
      modulosMap[modulo.idModulo] = modulo;
      console.log(`  📦 Módulo ID ${modulo.idModulo}: ${modulo.nombre}`);
    });
    
    // Mapeo de nombres de módulo a permisos
    const moduloToPermisoMap = {
      'bienvenida': 'ver_bienvenida',
      'dashboard': 'ver_dashboard',
      'servicios': 'ver_servicios',
      'órdenes': 'ver_ordenes',
      'categorías': 'ver_categorias',
      'productos': 'ver_productos',
      'ventas': 'ver_ventas',
      'clientes': 'ver_clientes',
      'proveedores': 'ver_proveedores',
      'cajas': 'ver_cajas',
      'tasa cambio': 'ver_tasa_cambio',
      'tasa de cambio': 'ver_tasa_cambio',
      'métodos de pago': 'ver_metodos_pago',
      'pagos': 'ver_pagos',
      'usuarios': 'ver_usuarios',
      'roles': 'ver_roles',
      'soporte técnico': 'ver_configuracion',
      'configuración': 'ver_configuracion',
      'módulos': 'ver_modulos',
      'permisos': 'ver_permisos',
      'reporte de productos': 'ver_reporte_productos',
      'reporte de ventas': 'ver_reporte_ventas',
      'reporte de usuarios': 'ver_reporte_usuarios',
      'reporte de pagos': 'ver_reporte_pagos',
      'reporte de servicios': 'ver_reporte_servicios',
      'reporte de proveedores': 'ver_reporte_proveedores',
      'reporte de categorías': 'ver_reporte_categorias',
      'reporte de recepciones': 'ver_reporte_ordenes',
      'estado de cuenta': 'ver_estado_cuenta',
      'estado de pago': 'ver_estado_pago',
      'estado de recepción': 'ver_estado_recepcion',
      'venta a crédito': 'realizar_venta_credito',
      'cuotas de crédito': 'ver_cuotas_credito',
      'órdenes de compra': 'ver_ordenes_compra',
      'kardex': 'ver_productos',
      'salida herramientas': 'ver_productos',
      'vendedores': 'ver_usuarios',
      'técnicos': 'ver_usuarios'
    };
    
    // Procesar permisos asignados
    console.log('🔍 Procesando permisos asignados:');
    permisosData.forEach((permiso, index) => {
      // console.log(`  Permiso ${index + 1}:`, permiso); // Comentado para reducir ruido
      
      if (permiso.asignado) {
        const modulo = modulosMap[permiso.idModulo];
        const nombreAccion = (permiso.nombre_accion || permiso.nombre || '').toLowerCase().trim();
        
        console.log(`    ✅ PERMISO ASIGNADO: ${nombreAccion}`);
        
        // Guardar el permiso exacto
        if (nombreAccion) {
          permisos[nombreAccion] = true;
          // Alias comunes si el backend usa nombres diferentes
          if (nombreAccion === 'ventas') permisos['ver_ventas'] = true;
          if (nombreAccion === 'productos') permisos['ver_productos'] = true;
          if (nombreAccion === 'ordenes' || nombreAccion === 'órdenes') permisos['ver_ordenes'] = true;
        }
        
        // Mapear por módulo si existe
        if (modulo && modulo.nombre) {
          const nombreModulo = modulo.nombre.toLowerCase().trim();
          const permisoBasico = moduloToPermisoMap[nombreModulo];
          if (permisoBasico) {
            permisos[permisoBasico] = true;
            console.log(`       📌 Mapeado module-to-perm: ${nombreModulo} -> ${permisoBasico}`);
          }
        }
      }
    });

    // --- MEJORA: MERGE CON PERMISOS POR DEFECTO ---
    // Si no es admin (que tiene todo), aplicar defaults como base si la API devolvió poco
    try {
      const { getDefaultPermissions } = await import('@/config/menuPermissions');
      const defaultPerms = getDefaultPermissions(idRol); // Usamos la versión sincrónica directa
      
      console.log('🔄 Mezclando con permisos por defecto para rol:', idRol);
      Object.keys(defaultPerms).forEach(key => {
        if (defaultPerms[key] === true && !permisos[key]) {
          permisos[key] = true;
          console.log(`       ➕ Agregado por defecto (fallback): ${key}`);
        }
      });
    } catch (e) {
      console.warn('⚠️ No se pudieron cargar defaultPerms para mezclar:', e.message);
    }
    // ----------------------------------------------
    
    // Si la API no devolvió módulos (0 módulos activos), intentar mapear permisos basados 
    // en nombres de acción comunes como fallback de emergencia
    if (modulos.length === 0) {
      console.warn('⚠️ No se recibieron módulos de la API. Aplicando mapeo de emergencia por nombre de acción.');
      permisosData.forEach(p => {
        if (p.asignado) {
          const action = (p.nombre_accion || p.nombre || '').toLowerCase();
          if (action.includes('venta')) permisos['ver_ventas'] = true;
          if (action.includes('producto')) permisos['ver_productos'] = true;
          if (action.includes('cliente')) permisos['ver_clientes'] = true;
          if (action.includes('caja')) permisos['ver_cajas'] = true;
          if (action.includes('pago')) permisos['ver_pagos'] = true;
          if (action.includes('orden')) permisos['ver_ordenes'] = true;
          if (action.includes('servicio')) permisos['ver_servicios'] = true;
        }
      });
    }

    // Si es administrador (idRol === 1 o idRol === 2 según SQL), asegurar todos los permisos
    if (idRol === 1 || idRol === 2) {
      console.log('👑 Usuario administrativo, otorgando todos los permisos');
      
      const adminPermissions = {
        ver_bienvenida: true, ver_dashboard: true, ver_servicios: true, ver_ordenes: true,
        ver_categorias: true, ver_productos: true, ver_ventas: true, ver_clientes: true,
        ver_proveedores: true, ver_cajas: true, ver_tasa_cambio: true, ver_metodos_pago: true,
        ver_pagos: true, ver_usuarios: true, ver_roles: true, ver_configuracion: true,
        crear_orden: true, crear_venta: true, crear_producto: true, crear_cliente: true,
        editar_orden: true, editar_venta: true, editar_producto: true,
        eliminar_orden: true, eliminar_venta: true,
        bienvenida: true, dashboard: true, servicios: true, ordenes: true, categorias: true,
        productos: true, ventas: true, clientes: true, proveedores: true, cajas: true,
        tasa_cambio: true, metodos_pago: true, pagos: true, usuarios: true, roles: true,
        configuracion: true, soporte: true
      };
      
      Object.assign(permisos, adminPermissions);
    }
    
    // 🔥 FORZAR ACCESO A SERVICIOS PARA TÉCNICOS (Rol ID 5 o 3)
    if (idRol === 5 || idRol === 3) {
      console.log('🛠️ Forzando permisos de Taller para rol Técnico...');
      const servicePermissions = {
        ver_servicios: true,
        ver_ordenes: true,
        acceso_nueva_orden: true,
        crear_orden: true,
        editar_orden: true,
        eliminar_orden: true,
        ver_producto_servicio: true,
        crear_producto_servicio: true,
        editar_producto_servicio: true,
        eliminar_producto_servicio: true,
        confirmar_recepcion: true,
        servicios: true,
        ordenes: true,
        ver_clientes: true,
        crear_cliente: true,
        ver_reporte_servicios: true
      };
      Object.assign(permisos, servicePermissions);
    }
    
    // Asegurar que siempre tenga al menos bienvenida y dashboard
    if (!permisos.ver_bienvenida && !permisos.bienvenida) {
      permisos.ver_bienvenida = true;
      permisos.bienvenida = true;
    }
    if (!permisos.ver_dashboard && !permisos.dashboard) {
      permisos.ver_dashboard = true;
      permisos.dashboard = true;
    }
    
    // Guardar en caché
    permisosCache = permisos;
    modulosCache = modulos;
    lastFetch = now;
    
    console.log('✅ PERMISOS FINALES CARGADOS:');
    console.table(permisos);
    
    return permisos;
    
  } catch (error) {
    console.error('❌ Error cargando permisos desde API:', error);
    console.error('❌ Stack trace:', error.stack);
    return null;
  }
};

/**
 * Obtener permisos cacheados
 */
export const getPermisosCache = () => {
  console.log('📦 Obteniendo permisos cacheados:', permisosCache);
  return permisosCache;
};

/**
 * Limpiar caché de permisos
 */
export const limpiarCachePermisos = () => {
  permisosCache = null;
  modulosCache = null;
  lastFetch = 0;
  console.log('🧹 Caché de permisos limpiado');
};

/**
 * Forzar recarga de permisos
 * @param {number} idRol - ID del rol
 * @returns {Promise} Permisos recargados
 */
export const recargarPermisos = async (idRol) => {
  console.log('🔄 Forzando recarga de permisos...');
  limpiarCachePermisos();
  return await cargarPermisosDesdeAPI(idRol, true);
};

export default {
  cargarPermisosDesdeAPI,
  getPermisosCache,
  limpiarCachePermisos,
  recargarPermisos
};